import { actionTypes, change, destroy } from "redux-form";

export function isIndexedFieldPath(name) {
  // FieldArray-managed paths always continue past the numeric segment (`.child` or
  // another `[index]`). Terminal `map[123]` keys — notably PaymentAllocation's
  // `payment_accounts[${account.id}]` — must still be cleared on unregister so a
  // removed account cannot linger in the submitted payload.
  return typeof name === "string" && /\[\d+\](?:\.|\[)/.test(name);
}

function hasLiveForm(getState, form) {
  const forms = getState()?.form;
  return forms != null && Object.hasOwn(forms, form);
}

// A straggler CHANGE/BLUR after unmount can leave an empty husk (or one with
// values but no registered fields). A slice that still has registered fields
// belongs to a generation that is mounted right now and must not be destroyed.
function isStragglerHusk(getState, form) {
  const slice = getState()?.form?.[form];
  if (slice == null) {
    return false;
  }
  const registered = slice.registeredFields;
  return registered == null || Object.keys(registered).length === 0;
}

function isFormResidue(formState) {
  if (formState == null) {
    return false;
  }
  const keys = Object.keys(formState);
  // Late UNREGISTER on a missing form materializes `{}`.
  if (keys.length === 0) {
    return true;
  }
  // Late CHANGE on a missing form materializes a values-only slice.
  return keys.length === 1 && keys[0] === "values";
}

// Redux Form 8 sets `destroyed = true` before dispatching DESTROY and silences
// field unregister after that, so a real unmount emits DESTROY alone. REGISTER_FIELD
// after DESTROY is therefore an unambiguous new generation, not a straggler.
// The remaining risk is a late CHANGE/BLUR (or similar) from an async callback
// that fires after unmount and recreates an empty husk.
const NEW_GENERATION_TYPES = new Set([actionTypes.INITIALIZE, actionTypes.REGISTER_FIELD]);

// Form names can be per-entity (`ExternalAccount-<id>`), so tracking is bounded
// to keep a long session from accumulating names it will never revisit. Sets are
// insertion-ordered and re-adding a name does not move it, so this evicts the
// name tracked longest.
const MAX_TRACKED_FORMS = 256;

function trackBounded(tracked, form, onEvict) {
  tracked.add(form);
  if (tracked.size > MAX_TRACKED_FORMS) {
    const evicted = tracked.values().next().value;
    tracked.delete(evicted);
    onEvict?.(evicted);
  }
}

export default ({ dispatch, getState }) => {
  const destroyedForms = new Set();
  // Names dropped from destroyedForms by the bound. Only these may have late
  // UNREGISTER/CHANGE residue swept — never a values-only form that was never
  // destroyed (programmatic change before registration).
  const forgottenDestroyedForms = new Set();

  const forgetDestroyed = (form) => {
    destroyedForms.delete(form);
    forgottenDestroyedForms.delete(form);
  };

  const rememberDestroyed = (form) => {
    forgottenDestroyedForms.delete(form);
    trackBounded(destroyedForms, form, (evicted) => {
      if (evicted !== form) {
        trackBounded(forgottenDestroyedForms, evicted);
      }
    });
  };

  return (next) => (action) => {
    if (action.type === actionTypes.DESTROY) {
      const actionForms = Array.isArray(action.meta?.form) ? action.meta.form : [action.meta?.form];
      for (const form of actionForms) {
        if (typeof form === "string") {
          rememberDestroyed(form);
        }
      }
      return next(action);
    }

    const form = action.meta?.form;
    if (typeof form === "string" && NEW_GENERATION_TYPES.has(action.type)) {
      forgetDestroyed(form);
    }

    if (action.type === actionTypes.UNREGISTER_FIELD) {
      const field = action.payload?.name;
      if (
        typeof form === "string" &&
        typeof field === "string" &&
        hasLiveForm(getState, form) &&
        !destroyedForms.has(form) &&
        !isIndexedFieldPath(field)
      ) {
        dispatch(change(form, field, null));
      }
    }

    const result = next(action);
    if (
      typeof form === "string" &&
      destroyedForms.has(form) &&
      !NEW_GENERATION_TYPES.has(action.type) &&
      hasLiveForm(getState, form) &&
      isStragglerHusk(getState, form)
    ) {
      dispatch(destroy(form));
    } else if (
      typeof form === "string" &&
      forgottenDestroyedForms.has(form) &&
      (action.type === actionTypes.UNREGISTER_FIELD || action.type === actionTypes.CHANGE) &&
      isFormResidue(getState()?.form?.[form])
    ) {
      // Redux Form materializes `{}` (unregister) or values-only slices (change) when
      // acting on a missing form. Only sweep that residue for names known to have been
      // destroyed and later evicted from active destroy tracking.
      forgottenDestroyedForms.delete(form);
      dispatch(destroy(form));
    }
    return result;
  };
};
